import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2, User, Phone, ScanLine, Copy, ListChecks, Plus, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../ui/sonner';
import { OrnDivider } from './Ornaments';
import { WhatsappButton, WhatsAppGlyph } from './CtaButtons';
import BlessingCard from './BlessingCard';
import { UPI_VPA, whatsappForSelection } from '../../lib/constants';
import { track, getSessionId, getSource } from '../../lib/analytics';
import { openSevaPicker } from './SevaPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReserveSeva = () => {
  const [form, setForm] = useState({ name: '', phone: '', gotra: '' });
  const [selection, setSelection] = useState([]); // [{ name, displayName, amount, price, custom }]
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const total = selection.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const sevaSummary = selection.map((s) => s.displayName || s.name).join(', ');

  const pickSevas = () => {
    const initial = selection.reduce((acc, s) => {
      acc[s.name] = { selected: true, amount: Number(s.amount) || 0 };
      return acc;
    }, {});
    openSevaPicker('reserve-form', {
      initial,
      onSelect: (list) => {
        setSelection(list);
        toast.success(`${list.length} seva${list.length > 1 ? 's' : ''} selected.`);
      },
    });
  };

  const removeSeva = (name) => setSelection((cur) => cur.filter((s) => s.name !== name));

  const upiLink = () => {
    const params = new URLSearchParams({
      pa: UPI_VPA,
      pn: 'Omvedha',
      tn: `Sai Baba Mahotsava - ${sevaSummary || 'Seva'}`.slice(0, 80),
    });
    if (total) params.append('am', String(total));
    params.append('cu', 'INR');
    return `upi://pay?${params.toString()}`;
  };

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(UPI_VPA);
      toast.success('UPI ID copied — paste it in your UPI app.');
    } catch {
      toast.error('Could not copy. Please note the UPI ID manually.');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please enter your name and phone number.');
      return;
    }
    if (!selection.length) {
      toast.error('Please pick at least one seva.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        phone: form.phone,
        gotra: form.gotra,
        seva: sevaSummary,
        amount: String(total),
        preferred_date: '29-07-2026',
        source: getSource(),
        session_id: getSessionId(),
      });
      setDone(true);
      toast.success('Om Sai Ram! Your booking is received.');
      // Auto-open a thank-you WhatsApp chat so devotee lands in a live conversation.
      setTimeout(() => {
        const url = whatsappForSelection(selection, total);
        // Also prefix with the devotee's name/gotra for the priest.
        const withDetails = url.replace(
          'text=',
          `text=${encodeURIComponent(`Name: ${form.name}\nGotra: ${form.gotra || '-'}\nPhone: ${form.phone}\n\n`)}`
        );
        window.open(withDetails, '_blank', 'noopener,noreferrer');
        track('cta_whatsapp', 'reserve-auto-thankyou');
      }, 400);
    } catch (err) {
      toast.error('Something went wrong. Please try again or use WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  const personalWhatsapp = () => {
    if (!selection.length) {
      toast.error('Please pick at least one seva first.');
      return;
    }
    const url = whatsappForSelection(selection, total);
    const withDetails = url.replace(
      'text=',
      `text=${encodeURIComponent(`Name: ${form.name || '-'}\nGotra: ${form.gotra || '-'}\nPhone: ${form.phone || '-'}\n\n`)}`
    );
    track('cta_whatsapp', 'reserve-form');
    window.open(withDetails, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="reserve" data-testid="reserve-section" className="scroll-mt-24 bg-ivory py-14 md:py-20">
      <div className="mx-auto max-w-4xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <OrnDivider className="mb-5" />
          <h2 className="font-serif text-3xl tracking-tight text-ink md:text-4xl lg:text-5xl">
            Reserve Your Seva
          </h2>
          <p className="mx-auto mt-4 max-w-xl font-sans text-base text-ink/65">
            Share your details and our priests will connect with you to complete your Gurupurnima Sai Baba Mahotsava booking.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          className="mt-12 rounded-3xl border border-gold/25 bg-white p-7 shadow-[0_30px_70px_-45px_rgba(92,22,22,0.6)] md:p-10"
        >
          {done ? (
            <div className="flex flex-col items-center py-8 text-center" data-testid="reserve-success">
              <CheckCircle2 className="h-14 w-14 text-whatsapp" strokeWidth={1.5} />
              <h3 className="mt-4 font-serif text-2xl text-ink">Om Sai Ram, {form.name.split(' ')[0]}!</h3>
              <p className="mt-2 max-w-md font-sans text-sm text-ink/65">
                Your booking has been received. We&rsquo;ve opened a WhatsApp chat for you — just tap send. Scan the UPI QR below to complete your offering in one tap.
              </p>

              <div className="mt-6 w-full max-w-sm rounded-2xl border border-gold/30 bg-[#FBF3D8]/50 p-5 text-left">
                <dl className="space-y-2 font-sans text-sm">
                  <div className="flex flex-col gap-1">
                    <dt className="text-ink/55">Sevas</dt>
                    <dd className="font-medium text-ink">
                      <ul className="space-y-1">
                        {selection.map((s) => (
                          <li key={s.name} className="flex justify-between gap-3">
                            <span>{s.displayName || s.name}</span>
                            <span className="text-maroon">₹{Number(s.amount).toLocaleString('en-IN')}</span>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-gold/25 pt-2"><dt className="text-ink/55">Date</dt><dd className="font-medium text-ink text-right">29 Jul 2026</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-ink/55">Total Offering</dt><dd className="font-serif text-lg text-maroon text-right">₹{total.toLocaleString('en-IN')}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-ink/55">Venue</dt><dd className="font-medium text-ink text-right">Balakrishna Bayalu Ranga Mandira, R R Nagar</dd></div>
                </dl>
              </div>

              {/* UPI QR pay block */}
              <div
                data-testid="upi-qr-card"
                className="mt-6 flex w-full max-w-sm flex-col items-center rounded-2xl border border-gold/30 bg-white p-5"
              >
                <div className="flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.16em] text-gold-deep">
                  <ScanLine className="h-3.5 w-3.5" /> Scan &amp; Pay via UPI
                </div>
                <div className="mt-3 rounded-xl border border-gold/25 bg-white p-3">
                  <QRCodeSVG
                    data-testid="upi-qr"
                    value={upiLink()}
                    size={168}
                    level="M"
                    includeMargin={false}
                    bgColor="#ffffff"
                    fgColor="#1A1514"
                  />
                </div>
                <p className="mt-3 font-serif text-lg text-ink">
                  {total ? `₹${total.toLocaleString('en-IN')}` : 'Offering'} <span className="font-sans text-xs text-ink/50">to</span>{' '}
                  <span className="font-sans text-sm font-semibold text-maroon">Omvedha</span>
                </p>
                <button
                  type="button"
                  data-testid="upi-copy-btn"
                  onClick={copyUpi}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-gold/30 px-3 py-1.5 font-mono text-xs text-ink/75 transition-colors hover:bg-gold/10"
                >
                  <Copy className="h-3 w-3" /> {UPI_VPA}
                </button>
                <p className="mt-2 font-sans text-[11px] text-ink/45">
                  Works with GPay, PhonePe, Paytm &amp; every UPI app.
                </p>
              </div>

              <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                <button
                  data-testid="reserve-whatsapp-continue"
                  onClick={personalWhatsapp}
                  className="inline-flex items-center gap-2.5 rounded-full bg-whatsapp px-8 py-4 font-sans text-sm font-semibold uppercase tracking-[0.15em] text-white"
                >
                  <WhatsAppGlyph className="h-5 w-5" /> Confirm on WhatsApp
                </button>
              </div>

              <BlessingCard name={form.name} seva={sevaSummary} />
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reserve-form" className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="font-sans text-xs uppercase tracking-[0.12em] text-gold-deep">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                  <Input data-testid="reserve-name" value={form.name} onChange={set('name')} placeholder="Your name" className="border-gold/30 bg-ivory pl-9" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="font-sans text-xs uppercase tracking-[0.12em] text-gold-deep">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                  <Input data-testid="reserve-phone" value={form.phone} onChange={set('phone')} placeholder="+91 XXXXXXXXXX" className="border-gold/30 bg-ivory pl-9" />
                </div>
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <Label className="font-sans text-xs uppercase tracking-[0.12em] text-gold-deep">Gotra (optional)</Label>
                <Input data-testid="reserve-gotra" value={form.gotra} onChange={set('gotra')} placeholder="Your gotra" className="border-gold/30 bg-ivory" />
              </div>

              {/* Multi-seva picker trigger */}
              <div className="space-y-1.5 md:col-span-2">
                <Label className="font-sans text-xs uppercase tracking-[0.12em] text-gold-deep">
                  Select Sevas
                </Label>
                <button
                  type="button"
                  data-testid="reserve-seva-picker-btn"
                  onClick={pickSevas}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-gold/30 bg-ivory px-4 py-3.5 text-left font-sans text-sm text-ink/70 transition-colors hover:border-gold hover:bg-gold/10"
                >
                  <span className="inline-flex items-center gap-2.5">
                    <ListChecks className="h-4 w-4 text-gold-deep" />
                    {selection.length
                      ? `${selection.length} seva${selection.length > 1 ? 's' : ''} selected · ₹${total.toLocaleString('en-IN')}`
                      : 'Tap to choose one or more sevas'}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-gold/30 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold-deep">
                    <Plus className="h-3 w-3" /> {selection.length ? 'Edit' : 'Add'}
                  </span>
                </button>
                {selection.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2" data-testid="reserve-seva-chips">
                    {selection.map((s) => (
                      <span
                        key={s.name}
                        data-testid={`reserve-chip-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                        className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-[#FBF3D8]/60 px-3 py-1 font-sans text-xs text-ink"
                      >
                        <span className="font-medium">{s.displayName || s.name}</span>
                        <span className="text-maroon">₹{Number(s.amount).toLocaleString('en-IN')}</span>
                        <button
                          type="button"
                          onClick={() => removeSeva(s.name)}
                          aria-label={`Remove ${s.name}`}
                          className="ml-0.5 text-ink/40 transition-colors hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-2 flex flex-col gap-4 md:col-span-2 md:flex-row md:items-center">
                <button
                  type="submit"
                  data-testid="reserve-submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2.5 rounded-full px-8 py-4 font-sans text-sm font-semibold uppercase tracking-[0.15em] text-ink disabled:opacity-60"
                  style={{ background: 'linear-gradient(180deg,#F5D97A,#D4AF37 45%,#B38B22)' }}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {loading ? 'Submitting…' : 'Reserve My Seva'}
                </button>
                <span className="font-sans text-sm text-ink/50">or</span>
                <WhatsappButton label="reserve-inline" />
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ReserveSeva;
;
